import { PROFILE_LABEL_SX } from "../profileFieldStyles";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DocumentText } from "iconsax-react";

import JobTabHeader from "./JobTabHeader";
import TabSectionHeader from "../TabSectionHeader";
import { popupFilterInputSx } from "../PopupMultiSelectField";
import SearchableSingleValueField from "../SearchableSingleValueField";
import CompanySizeDropdownField from "../CompanySizeDropdownField";
import { fetchHiringManagers, searchPlatformContacts } from "../../utils/hiringManagers";
import { isRecruiterContact } from "../../utils/recruiterOwnership";
import {
  getEntityLocationValues,
  normalizeLocationEntry,
  normalizeLocationFilterValues,
} from "../../utils/locationFilters";
import {
  normalizeIndustrySelectionValues,
} from "../../utils/industrySelections";
import {
  JOB_EMPLOYMENT_TYPE_OPTIONS,
  normalizeEmploymentType,
} from "../../utils/jobEmploymentTypes";
import {
  buildCompanySizeOptions,
  normalizeCompanySizeValue,
} from "../../utils/companySizeOptions";
import { buildCompanyProfileActions } from "../../utils/companySocialLinks";
import DOMPurify from "dompurify";
import RichTextField from "../RichTextField";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const PURPLE = "#9C4FFF";
const EDIT_MODE_BORDER = "#D9C2FF";
const EDIT_MODE_OUTLINE = "0 0 0 1px rgba(156,79,255,0.22)";
const LABEL_BLUE = "#3451F4";
const BORDER = "#D2D8DE";
const MUTED = "#A2ABB4";
const CHIP_BG = "#E4F5FF";
const TEXT = "#344054";
const MULTI_VALUE_SEPARATOR = " | ";
const WORK_ARRANGEMENT_OPTIONS = ["On-site", "Hybrid", "Remote"];
const NEED_SPONSORSHIP_OPTIONS = ["Yes", "No"];
const TITLE_OPTIONS = ["Recruiter / HR", "Hiring Manager", "Primary Contact", "Team Lead"];
const DESCRIPTION_SECTION_STOP_HEADINGS = [
  "responsibilities",
  "key responsibilities",
  "what you'll do",
  "what you will do",
  "what you’ll do",
  "requirements",
  "minimum qualifications",
  "preferred qualifications",
  "qualifications",
  "experience requirements",
  "education requirements",
  "technical expertise",
];
const US_STATE_LABEL_BY_CODE = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  DC: "District of Columbia",
};
const US_STATE_OPTIONS = Object.values(US_STATE_LABEL_BY_CODE);

const buildFieldSx = (editing = false) => ({
  "& .MuiOutlinedInput-root": {
    minHeight: 42,
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    boxShadow: editing ? EDIT_MODE_OUTLINE : "0 1px 2px rgba(16, 24, 40, 0.05)",
    "& fieldset": {
      borderColor: editing ? EDIT_MODE_BORDER : BORDER,
      borderWidth: 1,
    },
    "&:hover fieldset": { borderColor: editing ? PURPLE : "#BFC6D4" },
    "&.Mui-focused fieldset": { borderColor: editing ? PURPLE : BORDER, borderWidth: 1 },
  },
  "& .MuiAutocomplete-inputRoot": {
    alignItems: "center",
    pr: "12px !important",
    py: "0 !important",
  },
  "& .MuiOutlinedInput-input": {
    fontFamily: "Be Vietnam Pro",
    fontSize: 14,
    fontWeight: 400,
    color: TEXT,
    textAlign: "left",
    padding: "8px 16px",
  },
  "& .MuiAutocomplete-input": {
    minWidth: "0 !important",
    fontFamily: "Be Vietnam Pro",
    fontSize: 14,
    fontWeight: 400,
    color: TEXT,
    textAlign: "left",
    padding: "8px 16px !important",
  },
  "& .MuiSelect-select": {
    fontFamily: "Be Vietnam Pro",
    fontSize: 14,
    fontWeight: 400,
    color: TEXT,
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    padding: "8px 16px",
  },
  "& .MuiInputBase-input::placeholder": {
    color: MUTED,
    opacity: 1,
  },
});

const buildTextAreaSx = (editing = false) => ({
  ...buildFieldSx(editing),
  "& .MuiOutlinedInput-root": {
    ...buildFieldSx(editing)["& .MuiOutlinedInput-root"],
    minHeight: "unset",
    alignItems: "flex-start",
  },
  "& textarea": {
    fontFamily: "Be Vietnam Pro",
    fontSize: 14,
    fontWeight: 400,
    color: TEXT,
    lineHeight: "24px",
  },
});

const fieldSx = buildFieldSx(true);
const readOnlyFieldSx = buildFieldSx(false);
const readOnlyTextAreaSx = buildTextAreaSx(false);

const labelSx = {
  ...PROFILE_LABEL_SX,
  display: "block",
  width: "100%",
  mb: 2,
};

const chipSx = {
  height: 28,
  borderRadius: "999px",
  backgroundColor: CHIP_BG,
  color: LABEL_BLUE,
  fontWeight: 500,
  fontSize: 14,
  fontFamily: "Be Vietnam Pro",
  "& .MuiChip-deleteIcon": {
    color: LABEL_BLUE,
    fontSize: 16,
  },
};

const summaryCheckboxIcon = (
  <Box
    sx={{
      width: 24,
      height: 24,
      flexShrink: 0,
      borderRadius: "4px",
      border: "1px solid #D2D8DE",
      backgroundColor: "#FFFFFF",
      boxSizing: "border-box",
    }}
  />
);

const summaryCheckboxCheckedIcon = (
  <Box
    sx={{
      width: 24,
      height: 24,
      flexShrink: 0,
      borderRadius: "4px",
      border: "1px solid #D2D8DE",
      backgroundColor: "#FFFFFF",
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <Box
      component="span"
      sx={{
        width: 5,
        height: 9,
        borderRight: "1.75px solid #B8C1CC",
        borderBottom: "1.75px solid #B8C1CC",
        transform: "rotate(45deg) translate(-0.65px, -0.75px)",
        transformOrigin: "center",
        boxSizing: "border-box",
      }}
    />
  </Box>
);

const actionLinkSx = {
  fontFamily: "Be Vietnam Pro",
  fontSize: 14,
  fontWeight: 500,
  color: PURPLE,
  cursor: "pointer",
  userSelect: "none",
  "&:hover": {
    opacity: 0.88,
  },
};

function getAuthToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("jwt") ||
    sessionStorage.getItem("accessToken") ||
    sessionStorage.getItem("access_token") ||
    ""
  );
}

function getAuthHeaders() {
  const token = getAuthToken();
  if (!token) return {};

  const rawToken = String(token).replace(/^Bearer\s+/i, "").trim();
  return {
    Authorization: String(token).startsWith("Bearer ") ? String(token) : `Bearer ${token}`,
    "x-access-token": rawToken,
  };
}

function normalizeText(value = "") {
  const normalized = String(value || "").replace(/\s+/g, " ").trim();
  return normalized === "-" ? "" : normalized;
}

function narrativeValueToText(value) {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map((item) => narrativeValueToText(item)).filter(Boolean).join("\n");
  }
  if (typeof value === "object") {
    return Object.values(value).map((item) => narrativeValueToText(item)).filter(Boolean).join("\n");
  }
  return "";
}

function normalizeNarrativeText(value = "") {
  const normalized = narrativeValueToText(value)
    .replace(/\[object Object\]/g, "")
    .replace(/\n[ \t]*\n[ \t]*\n+/g, "\n\n")
    .trim();
  return normalized === "-" ? "" : normalized;
}

function hasHtmlMarkup(value = "") {
  return /<\/?[a-z][\s\S]*>/i.test(String(value || ""));
}

function normalizeSectionHeadingText(value = "") {
  return String(value || "")
    .toLowerCase()
    .replace(/&nbsp;/gi, " ")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isDescriptionStopHeading(value = "") {
  const normalized = normalizeSectionHeadingText(value);
  if (!normalized) return false;

  return DESCRIPTION_SECTION_STOP_HEADINGS.some(
    (heading) => normalized === heading || normalized.startsWith(`${heading} `)
  );
}

function extractDescriptionOnlyText(value = "") {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";

  const lines = rawValue.split(/\r?\n/);
  const keptLines = [];

  for (const line of lines) {
    if (isDescriptionStopHeading(line)) {
      break;
    }
    keptLines.push(line);
  }

  return keptLines.join("\n").trim();
}

function extractDescriptionOnlyHtml(value = "") {
  const rawValue = String(value || "").trim();
  if (!rawValue || typeof DOMParser === "undefined") return rawValue;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(rawValue, "text/html");
    const container = doc.createElement("div");
    let foundStopHeading = false;

    Array.from(doc.body.childNodes).forEach((node) => {
      if (foundStopHeading) return;

      const blockText = String(node?.textContent || "").trim();
      if (isDescriptionStopHeading(blockText)) {
        foundStopHeading = true;
        return;
      }

      container.appendChild(node.cloneNode(true));
    });

    return container.innerHTML.trim();
  } catch {
    return rawValue;
  }
}

function normalizeSkills(value = []) {
  return Array.from(
    new Set(
      (Array.isArray(value) ? value : [])
        .map((item) => normalizeText(item))
        .filter(Boolean)
    )
  );
}

function normalizeLookupValue(value = "") {
  return normalizeText(value)
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function uniqStrings(values = []) {
  return Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => normalizeText(value))
        .filter(Boolean)
    )
  );
}

function buildPrimaryContactOption(raw = {}) {
  const safeRaw =
    typeof raw === "string"
      ? { name: raw }
      : raw && typeof raw === "object"
        ? raw
        : {};
  const name = normalizeText(
    safeRaw?.name ??
      safeRaw?.contact_name ??
      safeRaw?.hiring_manager_name ??
      safeRaw?.primary_contact ??
      safeRaw?.label ??
      ""
  );
  if (!name || isRecruiterContact(safeRaw)) return null;

  const id = Number(safeRaw?.id ?? safeRaw?.contact_id ?? safeRaw?.hiring_manager_id);

  return {
    id: Number.isFinite(id) && id > 0 ? id : null,
    name,
    label: name,
    role: normalizeText(
      safeRaw?.role ??
        safeRaw?.title ??
        safeRaw?.position ??
        safeRaw?.contact_title ??
        safeRaw?.hiring_manager_title ??
        ""
    ),
    email: normalizeText(safeRaw?.email ?? safeRaw?.contact_email ?? ""),
    phone: normalizeText(safeRaw?.phone ?? safeRaw?.contact_phone ?? ""),
    profile_image: normalizeText(
      safeRaw?.profile_image ??
        safeRaw?.avatar_url ??
        safeRaw?.avatar ??
        safeRaw?.photo_url ??
        safeRaw?.image ??
        ""
    ),
    linkedin_url: normalizeText(safeRaw?.linkedin_url ?? safeRaw?.linkedin ?? ""),
    contact_kind: normalizeText(safeRaw?.contact_kind ?? ""),
  };
}

function getPrimaryContactOptionKey(option = {}) {
  const normalizedName = normalizeText(option?.name || option?.label || "").toLowerCase();
  const normalizedRole = normalizeText(option?.role || option?.title || "").toLowerCase();
  const normalizedEmail = normalizeText(option?.email || "").toLowerCase();

  if (option?.id != null && option.id !== "") return `id:${option.id}`;
  if (normalizedEmail) return `email:${normalizedEmail}`;
  return `${normalizedName}::${normalizedRole}`;
}

function mergePrimaryContactOptions(optionLists = []) {
  const seen = new Set();
  const nextOptions = [];

  (Array.isArray(optionLists) ? optionLists : [])
    .flatMap((list) => (Array.isArray(list) ? list : [list]))
    .forEach((item) => {
      const option = buildPrimaryContactOption(item);
      if (!option) return;

      const key = getPrimaryContactOptionKey(option);
      if (!key || seen.has(key)) return;
      seen.add(key);
      nextOptions.push(option);
    });

  return nextOptions;
}

function buildCompanyPrimaryContactOptions(company = {}, fallbackTitle = "Primary Contact") {
  return mergePrimaryContactOptions([
    Array.isArray(company?.contacts) ? company.contacts : [],
    Array.isArray(company?.hiringManagers)
      ? company.hiringManagers
      : Array.isArray(company?.hiring_managers)
        ? company.hiring_managers
        : [],
    {
      name: company?.contact_name || company?.contact_email || company?.contact_phone || "",
      role: company?.contact_role || company?.contact_title || fallbackTitle,
      email: company?.contact_email || "",
      phone: company?.contact_phone || "",
      contact_kind: "contact",
    },
    {
      name: company?.hiring_manager_name || company?.hiring_manager || "",
      role: company?.hiring_manager_role || company?.hiring_manager_title || "Hiring Manager",
      contact_kind: "hiring_manager",
    },
  ]);
}

function buildEmptyContactForm(initial = {}) {
  return {
    name: normalizeText(initial?.name || ""),
    role: normalizeText(initial?.role || ""),
    email: normalizeText(initial?.email || ""),
    phone: normalizeText(initial?.phone || ""),
  };
}

function splitLocationLabel(value = "") {
  const parts = normalizeText(value)
    .split(",")
    .map((part) => normalizeText(part))
    .filter(Boolean);

  const city = parts[0] || "";
  const statePart = parts[1] || "";
  const normalizedState =
    statePart && statePart.length === 2
      ? US_STATE_LABEL_BY_CODE[statePart.toUpperCase()] || statePart
      : statePart;

  return {
    city,
    state: normalizedState,
  };
}

function buildLocationValuesFromDraft(draft = {}) {
  const city = normalizeText(draft.locationCity);
  const state = normalizeText(draft.locationState);
  const baseLocation = city || state ? `${city}${city && state ? ", " : ""}${state}` : "";
  if (!baseLocation) return [];

  return normalizeLocationFilterValues([normalizeLocationEntry(baseLocation) || baseLocation]);
}

function buildLocationOptionParts(values = []) {
  const cities = [];
  const states = [];

  (Array.isArray(values) ? values : []).forEach((value) => {
    const normalizedLocation = normalizeLocationEntry(value) || normalizeText(value);
    const parts = splitLocationLabel(normalizedLocation);
    if (parts.city) cities.push(parts.city);
    if (parts.state) states.push(parts.state);
  });

  return {
    cityOptions: uniqStrings(cities),
    stateOptions: uniqStrings([...US_STATE_OPTIONS, ...states]),
  };
}

function buildJobDetailsDraft(data = {}) {
  const locationValues = getEntityLocationValues(data);
  const firstLocation = splitLocationLabel(locationValues[0] || data?.location || "");
  const industryValues = normalizeIndustrySelectionValues(
    data?.industries ??
      data?.industryValues ??
      data?.industry_sectors_json ??
      data?.industry
  );

  return {
    primaryContact: normalizeText(data?.primaryContact),
    titleField: normalizeText(data?.titleField || "Hiring Manager") || "Hiring Manager",
    salaryRange: normalizeText(data?.salaryRange),
    companyWebsite: normalizeText(data?.companyWebsite),
    careerWebsite: normalizeText(data?.careerWebsite),
    companySize: normalizeCompanySizeValue(data?.companySize),
    industry: normalizeText(industryValues[0] || data?.industry),
    industryValues,
    locationValues,
    locationCity: firstLocation.city,
    locationState: firstLocation.state,
    employmentType: normalizeEmploymentType(data?.employmentType),
    workArrangement: normalizeText(data?.workArrangement),
    needSponsorship: normalizeText(data?.needSponsorship),
    hardSkills: normalizeSkills(data?.hardSkills),
    softSkills: normalizeSkills(data?.softSkills),
    jobDescription: String(data?.jobDescription || "").trim(),
    responsibilities: String(data?.responsibilities || "").trim(),
    qualifications: String(data?.qualifications || "").trim(),
  };
}

function ReadOnlyTextField({ value = "", multiline = false, rows = 1, sx = undefined }) {
  return (
    <TextField
      fullWidth
      size="small"
      multiline={multiline}
      rows={rows}
      value={normalizeText(value) || (multiline ? "" : "-")}
      sx={[multiline ? readOnlyTextAreaSx : readOnlyFieldSx, sx]}
      InputProps={{ readOnly: true }}
    />
  );
}

function ReadOnlyNarrativeField({
  value = "",
  sx = undefined,
  trimToDescription = false,
}) {
  const normalizedValue = normalizeNarrativeText(value);
  const containsHtml = hasHtmlMarkup(normalizedValue);
  const displayValue = useMemo(() => {
    if (!trimToDescription) return normalizedValue;
    return containsHtml
      ? extractDescriptionOnlyHtml(normalizedValue)
      : extractDescriptionOnlyText(normalizedValue);
  }, [containsHtml, normalizedValue, trimToDescription]);
  const sanitizedMarkup = useMemo(() => {
    if (!containsHtml || !displayValue) return "";
    return DOMPurify.sanitize(displayValue);
  }, [containsHtml, displayValue]);

  return (
    <Box
      sx={[
        {
          ...readOnlyTextAreaSx,
          "& .MuiOutlinedInput-root": undefined,
          "& textarea": undefined,
          border: `1px solid ${BORDER}`,
          borderRadius: "8px",
          backgroundColor: "#FFFFFF",
          boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)",
          minHeight: 74,
          p: 3,
        },
        sx,
      ]}
    >
      {displayValue ? (
        containsHtml ? (
          <Box
            sx={{
              fontFamily: "Be Vietnam Pro",
              fontSize: 14,
              fontWeight: 400,
              color: TEXT,
              lineHeight: "24px",
              whiteSpace: "normal",
              "& p": { m: 0, mb: 1.2 },
              "& p:last-child": { mb: 0 },
              "& ul, & ol": { m: 0, pl: 2.5 },
              "& li + li": { mt: 0 },
              "& a": {
                color: PURPLE,
                textDecorationColor: PURPLE,
              },
            }}
            dangerouslySetInnerHTML={{ __html: sanitizedMarkup }}
          />
        ) : (
          <Typography
            sx={{
              fontFamily: "Be Vietnam Pro",
              fontSize: 14,
              fontWeight: 400,
              color: TEXT,
              lineHeight: "24px",
              whiteSpace: "pre-wrap",
            }}
          >
            {displayValue}
          </Typography>
        )
      ) : (
        <Typography
          sx={{
            fontFamily: "Be Vietnam Pro",
            fontSize: 14,
            fontWeight: 400,
            color: MUTED,
          }}
        >
          -
        </Typography>
      )}
    </Box>
  );
}

function ToggleOption({ label, checked, onChange, disabled = false }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Typography
        sx={{
          fontFamily: "Be Vietnam Pro",
          fontSize: 14,
          fontWeight: 400,
          color: TEXT,
        }}
      >
        {label}:
      </Typography>
      <Checkbox
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        disableRipple
        icon={summaryCheckboxIcon}
        checkedIcon={summaryCheckboxCheckedIcon}
        sx={{
          width: 24,
          height: 24,
          minWidth: 24,
          minHeight: 24,
          p: 0,
          color: "transparent",
          "&:hover": {
            backgroundColor: "transparent",
          },
          "&.Mui-checked": {
            color: "transparent",
          },
          "&.Mui-disabled": {
            opacity: 1,
          },
        }}
      />
    </Box>
  );
}

function SearchableChipCollectionField({
  label,
  value = [],
  onChange,
  options = [],
  editing = false,
  disabled = false,
  inputSx = undefined,
}) {
  const [showInput, setShowInput] = useState(false);
  const normalizedValues = useMemo(() => normalizeSkills(value), [value]);

  useEffect(() => {
    if (!editing) {
      setShowInput(false);
    }
  }, [editing]);

  const handleAddValue = useCallback(
    (nextValue) => {
      const normalized = normalizeText(nextValue);
      if (!normalized) {
        setShowInput(false);
        return;
      }

      onChange?.(normalizeSkills([...normalizedValues, normalized]));
      setShowInput(false);
    },
    [normalizedValues, onChange]
  );

  const handleDelete = useCallback(
    (targetValue) => {
      onChange?.(
        normalizedValues.filter(
          (item) => item.toLowerCase() !== normalizeText(targetValue).toLowerCase()
        )
      );
    },
    [normalizedValues, onChange]
  );

  return (
    <Box sx={{ mt: 2 }}>
      <Typography sx={labelSx}>{label}</Typography>

      {normalizedValues.length ? (
        <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap sx={{ mt: 0.85 }}>
          {normalizedValues.map((item) => (
            <Chip
              key={item}
              label={item}
              size="small"
              sx={chipSx}
              onDelete={editing ? () => handleDelete(item) : undefined}
            />
          ))}
        </Stack>
      ) : !editing ? (
        <Box
          sx={{
            mt: 0.85,
            minHeight: 40,
            borderRadius: "8px",
            border: `1px solid ${BORDER}`,
            backgroundColor: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            px: 1.4,
          }}
        >
          <Typography sx={{ fontSize: 12, color: MUTED, fontFamily: "Be Vietnam Pro" }}>-</Typography>
        </Box>
      ) : null}

      {editing ? (
        <Typography
          onClick={() => setShowInput((prev) => !prev)}
          sx={{ ...actionLinkSx, mt: 0.9, display: "block", whiteSpace: "nowrap" }}
        >
          {showInput ? "Close" : "Add New"}
        </Typography>
      ) : null}

      {editing && (showInput || !normalizedValues.length) ? (
        <Box sx={{ mt: 0.85 }}>
          <SearchableSingleValueField
            value=""
            onChange={handleAddValue}
            options={uniqStrings([...normalizedValues, ...(Array.isArray(options) ? options : [])])}
            loading={disabled}
            placeholder={`Search or add ${label.toLowerCase().replace(":", "")}`}
            inputSx={inputSx || popupFilterInputSx()}
            textFieldProps={{ fullWidth: true }}
            autocompleteProps={{
              openOnFocus: true,
              autoHighlight: true,
              disabled,
              forcePopupIcon: true,
              noOptionsText: "Type to add a new entry",
            }}
          />
        </Box>
      ) : null}
    </Box>
  );
}

export default function JobDetailsDetailsTab({
  data,
  fieldOptions = {},
  onEditHeader,
  onSaveDetails,
  onAddDocument,
  documentsUploading = false,
  documentsError = "",
  showDocuments = true,
}) {
  const entityLabel = "Job";
  const entityNoun = "job";
  const fileInputRef = useRef(null);
  const documents = Array.isArray(data?.documents) ? data.documents : [];
  const companyId = Number(data?.companyId || 0);
  const companyName = normalizeText(data?.companyName);
  const companyWebsite = normalizeText(data?.companyWebsite);

  const [companyContactOptions, setCompanyContactOptions] = useState([]);
  const [platformContactOptions, setPlatformContactOptions] = useState([]);
  const [loadingCompanyContacts, setLoadingCompanyContacts] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccessMessage, setSaveSuccessMessage] = useState("");
  const [draft, setDraft] = useState(() => buildJobDetailsDraft(data));
  const [primaryContactInputValue, setPrimaryContactInputValue] = useState(
    () => buildJobDetailsDraft(data).primaryContact
  );
  const [resolvedCompanyContactEntityId, setResolvedCompanyContactEntityId] = useState(
    Number.isFinite(companyId) && companyId > 0 ? companyId : null
  );
  const [addContactDialogOpen, setAddContactDialogOpen] = useState(false);
  const [addContactSaving, setAddContactSaving] = useState(false);
  const [addContactError, setAddContactError] = useState("");
  const [addContactForm, setAddContactForm] = useState(() => buildEmptyContactForm());

  const resetDraft = useCallback(() => {
    const nextDraft = buildJobDetailsDraft(data);
    setDraft(nextDraft);
    setPrimaryContactInputValue(nextDraft.primaryContact);
  }, [data]);

  useEffect(() => {
    if (editing) return;
    resetDraft();
  }, [editing, resetDraft]);

  const updateDraftField = useCallback((field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  }, []);

  const primaryContactOptions = useMemo(
    () =>
      mergePrimaryContactOptions([
        companyContactOptions,
        platformContactOptions,
        Array.isArray(data?.contacts) ? data.contacts : [],
        {
          name: draft.primaryContact || data?.primaryContact || "",
          role: draft.titleField || data?.titleField || "",
          email: data?.primaryContactEmail || "",
          phone: data?.primaryContactPhone || "",
          linkedin_url: data?.primaryContactLinkedin || "",
        },
      ]),
    [
      companyContactOptions,
      platformContactOptions,
      data?.contacts,
      data?.primaryContact,
      data?.primaryContactEmail,
      data?.primaryContactLinkedin,
      data?.primaryContactPhone,
      data?.titleField,
      draft.primaryContact,
      draft.titleField,
    ]
  );

  const selectedPrimaryContactOption = useMemo(() => {
    const normalizedValue = normalizeText(draft.primaryContact).toLowerCase();
    if (!normalizedValue) return null;

    return (
      primaryContactOptions.find(
        (option) =>
          normalizeText(option?.name || option?.label || "").toLowerCase() === normalizedValue
      ) || null
    );
  }, [draft.primaryContact, primaryContactOptions]);

  const titleOptions = useMemo(
    () =>
      uniqStrings([
        ...TITLE_OPTIONS,
        draft.titleField,
        data?.titleField,
        ...primaryContactOptions.map((option) => option?.role),
      ]),
    [data?.titleField, draft.titleField, primaryContactOptions]
  );

  const headerActions = useMemo(
    () =>
      buildCompanyProfileActions({
        ...data,
        website: data?.companyWebsite || data?.careerWebsite || data?.leadLink || data?.otherLink || "",
        company_website: data?.companyWebsite || "",
        contact_email:
          selectedPrimaryContactOption?.email ||
          data?.primaryContactEmail ||
          data?.companyEmail ||
          "",
        contact_phone:
          selectedPrimaryContactOption?.phone ||
          data?.primaryContactPhone ||
          data?.companyPhone ||
          "",
        linkedin_url:
          selectedPrimaryContactOption?.linkedin_url ||
          data?.primaryContactLinkedin ||
          data?.linkedinUrl ||
          "",
        facebook_url: data?.facebookUrl || data?.facebook_url || "",
        twitter_url: data?.twitterUrl || data?.twitter_url || data?.x_url || "",
        google_url: data?.googleUrl || data?.google_url || "",
        instagram_url: data?.instagramUrl || data?.instagram_url || "",
        youtube_url: data?.youtubeUrl || data?.youtube_url || "",
        tiktok_url: data?.tiktokUrl || data?.tiktok_url || "",
        social_links: data?.socialLinks || data?.social_links || [],
      }),
    [
      data,
      selectedPrimaryContactOption?.email,
      selectedPrimaryContactOption?.linkedin_url,
      selectedPrimaryContactOption?.phone,
    ]
  );

  const companySizeOptions = useMemo(
    () => buildCompanySizeOptions([draft.companySize]),
    [draft.companySize]
  );

  const industryOptions = useMemo(
    () =>
      uniqStrings([
        draft.industry,
        ...(Array.isArray(fieldOptions.industries) ? fieldOptions.industries : []),
      ]),
    [draft.industry, fieldOptions.industries]
  );

  const { cityOptions, stateOptions } = useMemo(
    () =>
      buildLocationOptionParts([
        ...(Array.isArray(fieldOptions.locations) ? fieldOptions.locations : []),
        ...(Array.isArray(draft.locationValues) ? draft.locationValues : []),
      ]),
    [draft.locationValues, fieldOptions.locations]
  );

  useEffect(() => {
    if (!saveSuccessMessage) return undefined;

    const timeoutId = window.setTimeout(() => {
      setSaveSuccessMessage("");
    }, 3200);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [saveSuccessMessage]);

  const handleStartEdit = useCallback(() => {
    resetDraft();
    setSaveError("");
    setSaveSuccessMessage("");
    setEditing(true);
    onEditHeader?.();
  }, [onEditHeader, resetDraft]);

  const handleSaveEdit = useCallback(async () => {
    if (!onSaveDetails) {
      setEditing(false);
      return;
    }

    const normalizedLocationValues = buildLocationValuesFromDraft(draft);
    const normalizedIndustryValue = normalizeText(draft.industry);
    const normalizedIndustryValues = normalizedIndustryValue ? [normalizedIndustryValue] : [];

    setSaving(true);
    setSaveError("");
    setSaveSuccessMessage("");

    try {
      await onSaveDetails({
        ...draft,
        primaryContact: normalizeText(draft.primaryContact),
        titleField: normalizeText(draft.titleField || "Hiring Manager") || "Hiring Manager",
        locationValues: normalizedLocationValues,
        location: normalizedLocationValues.join(MULTI_VALUE_SEPARATOR),
        industryValues: normalizedIndustryValues,
        industries: normalizedIndustryValues,
        industry: normalizedIndustryValue,
        employmentType: normalizeEmploymentType(draft.employmentType),
        workArrangement: normalizeText(draft.workArrangement),
        needSponsorship: normalizeText(draft.needSponsorship),
        companySize: normalizeText(draft.companySize),
        careerWebsite: normalizeText(draft.careerWebsite),
        hardSkills: normalizeSkills(draft.hardSkills),
        softSkills: normalizeSkills(draft.softSkills),
      });

      setSaveSuccessMessage(`${entityLabel} details saved.`);
      setEditing(false);
    } catch (error) {
      setSaveError(error?.message || `Failed to save ${entityNoun} details.`);
    } finally {
      setSaving(false);
    }
  }, [draft, entityLabel, entityNoun, onSaveDetails]);

  const handlePickDocument = useCallback(() => {
    if (documentsUploading) return;
    fileInputRef.current?.click();
  }, [documentsUploading]);

  const handleDocumentChange = useCallback(
    (event) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;
      onAddDocument?.(file);
    },
    [onAddDocument]
  );

  const openDocument = useCallback((url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  const handlePrimaryContactChange = useCallback(
    (newValue) => {
      if (typeof newValue === "string") {
        const normalized = normalizeText(newValue);
        setPrimaryContactInputValue(normalized);
        updateDraftField("primaryContact", normalized);
        return;
      }

      if (newValue?.inputValue) {
        const normalized = normalizeText(newValue.inputValue);
        setPrimaryContactInputValue(normalized);
        updateDraftField("primaryContact", normalized);
        return;
      }

      const nextName = normalizeText(newValue?.name || newValue?.label || "");
      const nextRole = normalizeText(newValue?.role || draft.titleField || "Hiring Manager");
      setPrimaryContactInputValue(nextName);
      updateDraftField("primaryContact", nextName);
      if (nextRole) {
        updateDraftField("titleField", nextRole);
      }
    },
    [draft.titleField, updateDraftField]
  );

  useEffect(() => {
    const query = normalizeText(primaryContactInputValue);
    if (!editing || query.length < 2) {
      setPlatformContactOptions([]);
      return undefined;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => {
      searchPlatformContacts({ search: query, signal: controller.signal })
        .then((rows) => setPlatformContactOptions(rows))
        .catch((error) => {
          if (error?.name !== "AbortError") setPlatformContactOptions([]);
        });
    }, 200);

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [editing, primaryContactInputValue]);

  const handleOpenAddContactDialog = useCallback(() => {
    if (!editing) return;
    setAddContactError("");
    setAddContactForm(
      buildEmptyContactForm({
        name: primaryContactInputValue || draft.primaryContact,
        role: draft.titleField || "Primary Contact",
      })
    );
    setAddContactDialogOpen(true);
  }, [draft.primaryContact, draft.titleField, editing, primaryContactInputValue]);

  const handleCloseAddContactDialog = useCallback(() => {
    if (addContactSaving) return;
    setAddContactDialogOpen(false);
    setAddContactError("");
  }, [addContactSaving]);

  const handleAddContactFieldChange = useCallback((field) => (event) => {
    const nextValue = event?.target?.value || "";
    setAddContactForm((prev) => ({ ...prev, [field]: nextValue }));
  }, []);

  const handleSaveNewContact = useCallback(async () => {
    const entityId =
      Number(resolvedCompanyContactEntityId || companyId || 0) > 0
        ? Number(resolvedCompanyContactEntityId || companyId || 0)
        : 0;
    const normalizedName = normalizeText(addContactForm.name);
    const normalizedRole = normalizeText(addContactForm.role || draft.titleField || "Primary Contact");

    if (!entityId) {
      setAddContactError(`Unable to identify the related company for this ${entityNoun}.`);
      return;
    }
    if (!normalizedName) {
      setAddContactError("Contact name is required.");
      return;
    }

    setAddContactSaving(true);
    setAddContactError("");

    try {
      const response = await fetch(`${API_BASE}/api/hiring-managers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        credentials: "include",
        body: JSON.stringify({
          entityType: "company",
          entityId,
          name: normalizedName,
          title: normalizedRole || "Primary Contact",
          email: normalizeText(addContactForm.email),
          phone: normalizeText(addContactForm.phone),
          contact_kind: "contact",
          source: "Job details",
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || `Failed to create company contact (${response.status})`);
      }

      const nextOption = buildPrimaryContactOption(payload?.hiringManager || {
        name: normalizedName,
        title: normalizedRole,
        email: addContactForm.email,
        phone: addContactForm.phone,
        contact_kind: "contact",
      });

      if (!nextOption) {
        throw new Error("Failed to create company contact.");
      }

      setCompanyContactOptions((prev) => mergePrimaryContactOptions([prev, [nextOption]]));
      setPrimaryContactInputValue(nextOption.name);
      updateDraftField("primaryContact", nextOption.name);
      updateDraftField("titleField", nextOption.role || normalizedRole || "Primary Contact");
      setAddContactDialogOpen(false);
    } catch (error) {
      setAddContactError(error?.message || "Failed to create company contact.");
    } finally {
      setAddContactSaving(false);
    }
  }, [
    addContactForm.email,
    addContactForm.name,
    addContactForm.phone,
    addContactForm.role,
    companyId,
    draft.titleField,
    entityNoun,
    resolvedCompanyContactEntityId,
    updateDraftField,
  ]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    const fetchCompanyProfile = async (targetCompanyId) => {
      if (!Number.isFinite(targetCompanyId) || targetCompanyId <= 0) return null;

      const response = await fetch(`${API_BASE}/api/companies/${targetCompanyId}`, {
        headers: getAuthHeaders(),
        credentials: "include",
        signal: controller.signal,
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || `Failed to load company contacts (${response.status})`);
      }

      return payload?.company || null;
    };

    const resolveCompanyIdFromSearch = async () => {
      const normalizedName = normalizeLookupValue(companyName);
      const searchSeeds = uniqStrings([companyName, companyWebsite, data?.companyDomain]);
      if (!searchSeeds.length) return null;

      const seenRows = new Set();
      const rows = [];

      for (const searchSeed of searchSeeds) {
        const response = await fetch(
          `${API_BASE}/api/companies?page=1&limit=100&search=${encodeURIComponent(searchSeed)}`,
          {
            headers: getAuthHeaders(),
            credentials: "include",
            signal: controller.signal,
          }
        );

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.error || `Failed to search companies (${response.status})`);
        }

        const nextRows = Array.isArray(payload?.rows) ? payload.rows : [];
        nextRows.forEach((row) => {
          const key = String(row?.id || row?.company_id || "");
          if (key && seenRows.has(key)) return;
          if (key) seenRows.add(key);
          rows.push(row);
        });
      }

      const matched =
        rows.find(
          (row) =>
            normalizeLookupValue(row?.name || row?.company_name || "") === normalizedName
        ) ||
        rows[0] ||
        null;

      return Number(matched?.id || matched?.company_id || 0) || null;
    };

    const loadCompanyContacts = async () => {
      setLoadingCompanyContacts(true);

      try {
        let profile = null;
        let resolvedProfileCompanyId = Number.isFinite(companyId) && companyId > 0 ? companyId : null;

        if (resolvedProfileCompanyId) {
          profile = await fetchCompanyProfile(resolvedProfileCompanyId);
        }

        if (!profile && (companyName || companyWebsite)) {
          const searchedCompanyId = await resolveCompanyIdFromSearch();
          if (searchedCompanyId) {
            resolvedProfileCompanyId = searchedCompanyId;
            profile = await fetchCompanyProfile(searchedCompanyId);
          }
        }

        if (!active) return;

        const companyEntityId =
          Number(profile?.id || profile?.company_id || resolvedProfileCompanyId || 0) || null;
        setResolvedCompanyContactEntityId(companyEntityId);
        const relatedLeadIds = (Array.isArray(profile?.related_leads) ? profile.related_leads : [])
          .map((lead) => Number(lead?.id || lead?.lead_id || 0))
          .filter((leadId) => Number.isFinite(leadId) && leadId > 0);

        const [companyHiringManagers, ...relatedLeadHiringManagers] = await Promise.all([
          companyEntityId
            ? fetchHiringManagers({
                apiBase: API_BASE,
                entityType: "company",
                entityId: companyEntityId,
                signal: controller.signal,
              }).catch(() => [])
            : Promise.resolve([]),
          ...relatedLeadIds.map((leadId) =>
            fetchHiringManagers({
              apiBase: API_BASE,
              entityType: "lead",
              entityId: leadId,
              signal: controller.signal,
            }).catch(() => [])
          ),
        ]);

        if (!active) return;

        setCompanyContactOptions(
          mergePrimaryContactOptions([
            buildCompanyPrimaryContactOptions(profile || {}, data?.titleField || "Primary Contact"),
            companyHiringManagers,
            relatedLeadHiringManagers.flat(),
          ])
        );
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error("Failed to load job detail company contacts:", error);
        if (active) {
          setResolvedCompanyContactEntityId(
            Number.isFinite(companyId) && companyId > 0 ? companyId : null
          );
          setCompanyContactOptions([]);
        }
      } finally {
        if (active) {
          setLoadingCompanyContacts(false);
        }
      }
    };

    loadCompanyContacts();

    return () => {
      active = false;
      controller.abort();
    };
  }, [companyId, companyName, companyWebsite, data?.companyDomain, data?.titleField]);

  return (
    <Box sx={{ backgroundColor: "#fff" }}>
      <JobTabHeader
        companyLogo={data.companyLogo}
        companyName={data.companyName}
        companyDomain={data.companyDomain}
        actions={headerActions}
        profileData={data}
        sx={{ mb: 2.5 }}
      />

      <TabSectionHeader
        title="Summary"
        rightText={saving ? "Saving..." : editing ? "Save" : "Edit"}
        onAction={editing ? handleSaveEdit : handleStartEdit}
        actionDisabled={saving}
        sx={{ py: 3, mb: 4 }}
        titleSx={{ fontSize: 20, fontWeight: 700 }}
        actionSx={{ color: PURPLE, fontWeight: 500 }}
      />

      {saveError ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {saveError}
        </Alert>
      ) : null}

      {saveSuccessMessage ? (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
          {saveSuccessMessage}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) minmax(0, 1fr)" },
          columnGap: 4,
          rowGap: 3,
        }}
      >
        <Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "minmax(0, 1fr) minmax(0, 1fr)",
              },
              gap: 4,
              alignItems: "start",
            }}
          >
            <Box>
              <Typography sx={labelSx}>Contact:</Typography>
              {editing ? (
                <Autocomplete
                  freeSolo
                  options={primaryContactOptions}
                  value={selectedPrimaryContactOption}
                  inputValue={primaryContactInputValue}
                  openOnFocus
                  autoHighlight
                  forcePopupIcon
                  disabled={saving}
                  onInputChange={(_, newInputValue) => {
                    setPrimaryContactInputValue(normalizeText(newInputValue));
                  }}
                  onChange={(_, newValue) => {
                    handlePrimaryContactChange(newValue);
                  }}
                  onBlur={() => {
                    const normalized = normalizeText(primaryContactInputValue);
                    setPrimaryContactInputValue(normalized);
                    updateDraftField("primaryContact", normalized);
                  }}
                  filterOptions={(options, state) => {
                    const query = normalizeLookupValue(state.inputValue);
                    const filtered = !query
                      ? options
                      : options.filter((option) =>
                          [
                            option?.name,
                            option?.role,
                            option?.email,
                            option?.phone,
                          ].some((value) => normalizeLookupValue(value).includes(query))
                        );

                    const normalizedInput = normalizeText(state.inputValue);
                    const exists = options.some(
                      (option) =>
                        normalizeText(option?.name || option?.label || "").toLowerCase() ===
                        normalizedInput.toLowerCase()
                    );

                    if (normalizedInput && !exists) {
                      filtered.push({
                        inputValue: normalizedInput,
                        name: `Add "${normalizedInput}"`,
                        label: `Add "${normalizedInput}"`,
                      });
                    }

                    return filtered;
                  }}
                  getOptionLabel={(option) => {
                    if (typeof option === "string") return option;
                    if (option?.inputValue) return option.inputValue;
                    return String(option?.name || option?.label || "");
                  }}
                  isOptionEqualToValue={(option, value) =>
                    getPrimaryContactOptionKey(option) === getPrimaryContactOptionKey(value)
                  }
                  noOptionsText={loadingCompanyContacts ? "Loading..." : "No contacts found"}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ display: "flex", alignItems: "center", gap: 1.1 }}>
                      {option?.inputValue ? (
                        <Typography
                          sx={{
                            fontFamily: "Be Vietnam Pro",
                            fontSize: 12,
                            fontWeight: 700,
                            color: PURPLE,
                          }}
                        >
                          {option?.label || option?.name}
                        </Typography>
                      ) : (
                        <>
                          <Avatar src={option?.profile_image || undefined} sx={{ width: 24, height: 24 }}>
                            {option?.name?.[0]}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontFamily: "Be Vietnam Pro",
                                fontSize: 12,
                                fontWeight: 700,
                                color: "#111827",
                              }}
                            >
                              {option?.name}
                            </Typography>
                            {!!(option?.role || option?.email) && (
                              <Typography
                                sx={{
                                  fontFamily: "Be Vietnam Pro",
                                  fontSize: 10.5,
                                  fontWeight: 600,
                                  color: MUTED,
                                }}
                              >
                                {[option?.role, option?.email].filter(Boolean).join(" | ")}
                              </Typography>
                            )}
                          </Box>
                        </>
                      )}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      placeholder="Search contacts"
                      autoComplete="off"
                      sx={fieldSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingCompanyContacts || saving ? (
                              <CircularProgress size={16} sx={{ mr: 0.5 }} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              ) : (
                <ReadOnlyTextField value={draft.primaryContact} />
              )}
            </Box>

            <Box>
              <Typography sx={labelSx}>Title:</Typography>
              {editing ? (
                <SearchableSingleValueField
                  value={draft.titleField}
                  onChange={(nextValue) => updateDraftField("titleField", normalizeText(nextValue))}
                  options={titleOptions}
                  loading={saving}
                  placeholder="Recruiter / HR"
                  inputSx={fieldSx}
                  textFieldProps={{ fullWidth: true }}
                  autocompleteProps={{
                    openOnFocus: true,
                    autoHighlight: true,
                    disabled: saving,
                    forcePopupIcon: true,
                  }}
                />
              ) : (
                <ReadOnlyTextField value={draft.titleField} />
              )}
            </Box>
          </Box>

          {editing ? (
            <Box sx={{ mt: 0.95 }}>
              <Typography onClick={handleOpenAddContactDialog} sx={actionLinkSx}>
                Add New
              </Typography>
            </Box>
          ) : null}

          <Box sx={{ mt: 4 }}>
            <Typography sx={labelSx}>Salary Range:</Typography>
            {editing ? (
              <TextField
                fullWidth
                size="small"
                value={draft.salaryRange}
                onChange={(event) => updateDraftField("salaryRange", event.target.value)}
                sx={fieldSx}
                disabled={saving}
              />
            ) : (
              <ReadOnlyTextField value={draft.salaryRange} />
            )}
          </Box>

          <Box sx={{ mt: 4 }}>
            <Typography sx={labelSx}>Location:</Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "minmax(0, 1fr) minmax(0, 1fr)",
                },
                gap: 4,
                alignItems: "start",
              }}
            >
              {editing ? (
                <>
                  <SearchableSingleValueField
                    value={draft.locationCity}
                    onChange={(nextValue) => updateDraftField("locationCity", normalizeText(nextValue))}
                    options={cityOptions}
                    loading={saving}
                    placeholder="City"
                    inputSx={fieldSx}
                    textFieldProps={{ fullWidth: true }}
                    autocompleteProps={{
                      openOnFocus: true,
                      autoHighlight: true,
                      disabled: saving,
                      forcePopupIcon: true,
                    }}
                  />
                  <SearchableSingleValueField
                    value={draft.locationState}
                    onChange={(nextValue) => updateDraftField("locationState", normalizeText(nextValue))}
                    options={stateOptions}
                    loading={saving}
                    placeholder="State"
                    inputSx={fieldSx}
                    textFieldProps={{ fullWidth: true }}
                    autocompleteProps={{
                      openOnFocus: true,
                      autoHighlight: true,
                      disabled: saving,
                      forcePopupIcon: true,
                    }}
                  />
                </>
              ) : (
                <>
                  <ReadOnlyTextField value={draft.locationCity} />
                  <ReadOnlyTextField value={draft.locationState} />
                </>
              )}
            </Box>
          </Box>

          <Box sx={{ mt: 4 }}>
            <Typography sx={labelSx}>Job Type / Workplace:</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, rowGap: 2, pt: 0.25 }}>
              {JOB_EMPLOYMENT_TYPE_OPTIONS.map((option) => (
                <ToggleOption
                  key={option}
                  label={option}
                  checked={draft.employmentType === option}
                  disabled={!editing || saving}
                  onChange={() =>
                    updateDraftField(
                      "employmentType",
                      draft.employmentType === option ? "" : option
                    )
                  }
                />
              ))}
              {WORK_ARRANGEMENT_OPTIONS.map((option) => (
                <ToggleOption
                  key={option}
                  label={option}
                  checked={draft.workArrangement === option}
                  disabled={!editing || saving}
                  onChange={() =>
                    updateDraftField(
                      "workArrangement",
                      draft.workArrangement === option ? "" : option
                    )
                  }
                />
              ))}
            </Box>
          </Box>

          <Box sx={{ mt: 2, maxWidth: { xs: "100%", sm: 235 } }}>
            <Typography sx={labelSx}>Need Sponsorship:</Typography>
            {editing ? (
              <TextField
                select
                fullWidth
                size="small"
                value={draft.needSponsorship}
                onChange={(event) => updateDraftField("needSponsorship", event.target.value)}
                sx={fieldSx}
                disabled={saving}
              >
                <MenuItem value="">
                  <em>-</em>
                </MenuItem>
                {NEED_SPONSORSHIP_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <ReadOnlyTextField value={draft.needSponsorship} />
            )}
          </Box>
        </Box>

        <Box>
          <Box>
            <Typography sx={labelSx}>Company Career Website:</Typography>
            {editing ? (
              <TextField
                fullWidth
                size="small"
                value={draft.careerWebsite}
                onChange={(event) => updateDraftField("careerWebsite", event.target.value)}
                sx={fieldSx}
                disabled={saving}
              />
            ) : (
              <ReadOnlyTextField value={draft.careerWebsite} />
            )}
          </Box>

          <Box sx={{ mt: 4 }}>
            <Typography sx={labelSx}>Company size:</Typography>
            {editing ? (
              <CompanySizeDropdownField
                value={draft.companySize}
                onChange={(nextValue) => updateDraftField("companySize", nextValue)}
                options={companySizeOptions}
                loading={saving}
                placeholder="50 - 200 employees"
                inputSx={fieldSx}
                textFieldProps={{ fullWidth: true }}
                autocompleteProps={{
                  openOnFocus: true,
                  autoHighlight: true,
                  disabled: saving,
                  forcePopupIcon: true,
                }}
              />
            ) : (
              <ReadOnlyTextField value={draft.companySize} />
            )}
          </Box>

          <Box sx={{ mt: 4 }}>
            <Typography sx={labelSx}>Industry:</Typography>
            {editing ? (
              <SearchableSingleValueField
                value={draft.industry}
                onChange={(nextValue) => {
                  const normalized = normalizeText(nextValue);
                  updateDraftField("industry", normalized);
                  updateDraftField("industryValues", normalized ? [normalized] : []);
                }}
                options={industryOptions}
                loading={saving}
                placeholder="Personal Products"
                inputSx={fieldSx}
                textFieldProps={{ fullWidth: true }}
                autocompleteProps={{
                  openOnFocus: true,
                  autoHighlight: true,
                  disabled: saving,
                  forcePopupIcon: true,
                }}
              />
            ) : (
              <ReadOnlyTextField value={draft.industry} />
            )}
          </Box>

          <SearchableChipCollectionField
            label="Hard skills:"
            value={draft.hardSkills}
            onChange={(nextValue) => updateDraftField("hardSkills", normalizeSkills(nextValue))}
            options={fieldOptions.hardSkills || fieldOptions.skills || []}
            editing={editing}
            disabled={saving}
            inputSx={fieldSx}
          />

          <SearchableChipCollectionField
            label="Soft skills:"
            value={draft.softSkills}
            onChange={(nextValue) => updateDraftField("softSkills", normalizeSkills(nextValue))}
            options={fieldOptions.softSkills || fieldOptions.skills || []}
            editing={editing}
            disabled={saving}
            inputSx={fieldSx}
          />
        </Box>
      </Box>

      <Box sx={{ mt: 3.2 }}>
        <TabSectionHeader
          title="Description"
          rightText={saving ? "Saving..." : editing ? "Save" : "Edit"}
          onAction={editing ? handleSaveEdit : handleStartEdit}
          actionDisabled={saving}
          sx={{ py: 3, mb: 4 }}
          titleSx={{ fontSize: 20, fontWeight: 700 }}
          actionSx={{ color: PURPLE, fontWeight: 500 }}
        />

        <Box sx={{ display: "grid", gap: 4 }}>
          <Box>
           
            {editing ? (
              <RichTextField
                label="Description"
                rows={5}
                value={draft.jobDescription}
                onChange={(html) => updateDraftField("jobDescription", html)}
                disabled={saving}
              />
            ) : (
              <ReadOnlyNarrativeField
                value={draft.jobDescription}
                trimToDescription={true}
              />
            )}
          </Box>

          <Box>
            <Typography sx={labelSx}>Key Responsibilities:</Typography>
            {editing ? (
              <RichTextField
                label="Key Responsibilities"
                rows={6}
                value={draft.responsibilities}
                onChange={(html) => updateDraftField("responsibilities", html)}
                disabled={saving}
              />
            ) : (
              <ReadOnlyNarrativeField value={draft.responsibilities} />
            )}
          </Box>

          <Box>
            <Typography sx={labelSx}>Qualifications:</Typography>
            {editing ? (
              <RichTextField
                label="Qualifications"
                rows={6}
                value={draft.qualifications}
                onChange={(html) => updateDraftField("qualifications", html)}
                disabled={saving}
              />
            ) : (
              <ReadOnlyNarrativeField value={draft.qualifications} />
            )}
          </Box>
        </Box>
      </Box>

      {showDocuments ? (
        <Box sx={{ mt: 3.2 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            style={{ display: "none" }}
            onChange={handleDocumentChange}
          />

          <TabSectionHeader
            title="Documents"
            rightText={documentsUploading ? "Uploading..." : "Add New Document"}
            onAction={documentsUploading || !onAddDocument ? undefined : handlePickDocument}
            actionDisabled={documentsUploading || !onAddDocument}
            sx={{ py: 3, mb: 4 }}
            titleSx={{ fontSize: 20, fontWeight: 700 }}
            actionSx={{ fontSize: 12, fontWeight: 500 }}
          />

          {!!documentsError && (
            <Typography
              sx={{
                mt: -0.5,
                mb: 1.2,
                fontFamily: "Be Vietnam Pro",
                fontSize: 11,
                fontWeight: 700,
                color: "#DC2626",
              }}
            >
              {documentsError}
            </Typography>
          )}

          {documents.length ? (
            documents.map((doc, index) => (
              <Box
                key={`${doc.url || doc.name || "document"}-${index}`}
                sx={{
                  border: `1px solid ${PURPLE}`,
                  borderRadius: 2,
                  px: 1.25,
                  py: 1,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  maxWidth: 420,
                  cursor: doc.url ? "pointer" : "default",
                }}
                onClick={() => openDocument(doc.url)}
              >
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: 2,
                      backgroundColor: "#F5F3FF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flex: "0 0 auto",
                    }}
                  >
                    <DocumentText size="16" color={PURPLE} variant="Outline" />
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: "Be Vietnam Pro",
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: "#111827",
                      }}
                      noWrap
                    >
                      {doc.name || `Document ${index + 1}`}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Be Vietnam Pro",
                        fontSize: 10,
                        fontWeight: 500,
                        color: MUTED,
                      }}
                      noWrap
                    >
                      {doc.meta || "100% uploaded"}
                    </Typography>
                  </Box>
                </Box>

                <Button
                  variant="text"
                  sx={{
                    minWidth: 0,
                    width: 28,
                    height: 28,
                    borderRadius: "999px",
                    color: PURPLE,
                    "&:hover": { backgroundColor: "#F5F3FF" },
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    openDocument(doc.url);
                  }}
                  disabled={!doc.url}
                >
                  <DocumentText size="16" color={PURPLE} variant="Outline" />
                </Button>
              </Box>
            ))
          ) : (
            <Typography
              sx={{
                mt: -0.25,
                fontFamily: "Be Vietnam Pro",
                fontSize: 11,
                fontWeight: 500,
                color: MUTED,
              }}
            >
              No documents uploaded yet.
            </Typography>
          )}
        </Box>
      ) : null}

      <Dialog open={addContactDialogOpen} onClose={handleCloseAddContactDialog} fullWidth maxWidth="xs">
        <DialogTitle data-popup-header sx={{ fontFamily: "Be Vietnam Pro", fontWeight: 800 }}>
          Add Company Contact
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          {addContactError ? (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {addContactError}
            </Alert>
          ) : null}

          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <TextField
              label="Contact Name"
              value={addContactForm.name}
              onChange={handleAddContactFieldChange("name")}
              fullWidth
              size="small"
              sx={fieldSx}
              disabled={addContactSaving}
            />
            <TextField
              label="Role"
              value={addContactForm.role}
              onChange={handleAddContactFieldChange("role")}
              fullWidth
              size="small"
              sx={fieldSx}
              disabled={addContactSaving}
            />
            <TextField
              label="Email"
              value={addContactForm.email}
              onChange={handleAddContactFieldChange("email")}
              fullWidth
              size="small"
              sx={fieldSx}
              disabled={addContactSaving}
            />
            <TextField
              label="Phone"
              value={addContactForm.phone}
              onChange={handleAddContactFieldChange("phone")}
              fullWidth
              size="small"
              sx={fieldSx}
              disabled={addContactSaving}
            />
          </Stack>
        </DialogContent>
        <DialogActions data-popup-footer sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleCloseAddContactDialog} disabled={addContactSaving} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveNewContact}
            disabled={addContactSaving}
            variant="contained"
            sx={{
              textTransform: "none",
              background: PURPLE,
              "&:hover": { background: "#8B3DFF" },
            }}
          >
            {addContactSaving ? "Saving..." : "Add Contact"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
